import React, {
  Fragment,
} from 'react';
import {
  notEmptyString,
} from '@populateui/utils';
import {
  EligibilityOverviewError,
} from 'dtos/eligibilityOverviewError';
import Section from 'views/EMR/components/Eligibility/Overview/Section';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlineOutlined';
import useUserInfo from 'pages/Dashboard/hooks/useUserInfo';

type Props = {
  errors: EligibilityOverviewError[];
};

type ErrorRowProps = {
  error: EligibilityOverviewError;
};

type InlineTextProps = {
  text: string;
};

type InlineSegment =
  | { type: 'text'; value: string; key: string }
  | { type: 'code'; value: string; key: string }
  | { type: 'link'; label: string; href: string; key: string };

function parseInlineSegments(text: string): InlineSegment[] {
  const segments: InlineSegment[] = [];
  const pattern = /(`[^`]+`|\[[^\]]+\]\([^)]+\))/g;
  const matches = Array.from(text.matchAll(pattern));
  let lastIndex = 0;

  matches.forEach((match) => {
    const matchIndex = match.index ?? 0;
    if (matchIndex > lastIndex) {
      segments.push({ type: 'text', value: text.slice(lastIndex, matchIndex), key: `text-${lastIndex}` });
    }
    const token = match[0];
    if (token.startsWith('`')) {
      segments.push({ type: 'code', value: token.slice(1, -1), key: `code-${matchIndex}` });
    } else {
      const linkMatch = token.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
      if (linkMatch) {
        segments.push({ type: 'link', label: linkMatch[1], href: linkMatch[2], key: `link-${matchIndex}` });
      }
    }
    lastIndex = matchIndex + token.length;
  });

  if (lastIndex < text.length) {
    segments.push({ type: 'text', value: text.slice(lastIndex), key: `text-${lastIndex}` });
  }

  return segments;
}

function InlineText({ text }: InlineTextProps) {
  const segments = parseInlineSegments(text);
  return (
    <>
      {segments.map((segment) => {
        if (segment.type === 'code') {
          return (
            <span key={segment.key} className="font-mono bg-gray-100 px-1 rounded">
              {segment.value}
            </span>
          );
        }
        if (segment.type === 'link') {
          return (
            <a
              key={segment.key}
              href={segment.href}
              target="_blank"
              rel="noreferrer"
              className="underline text-blue-600 hover:text-blue-800"
            >
              {segment.label}
            </a>
          );
        }
        return <Fragment key={segment.key}>{segment.value}</Fragment>;
      })}
    </>
  );
}

function ErrorRow({ error }: ErrorRowProps) {
  const {
    code,
    description,
    followupAction,
    possibleResolutions,
  } = error;

  const hasPrimaryLine = notEmptyString(code) || notEmptyString(description);
  const userInfo = useUserInfo();
  const mayViewClearinghouseTechnicals = userInfo?.flags?.mayViewClearinghouseTechnicals ?? false;

  const bullets = mayViewClearinghouseTechnicals && notEmptyString(possibleResolutions)
    ? possibleResolutions!.split('•').map((sentence) => sentence.trim()).filter(notEmptyString)
    : [];

  return (
    <div className="flex flex-col gap-0.5 py-2 border-b border-gray-100 last:border-b-0">
      {hasPrimaryLine && (
        <span className="text-xs font-semibold text-gray-700">
          {[code, description].filter(notEmptyString).join(' – ')}
        </span>
      )}
      {notEmptyString(followupAction) && (
        <span className="text-xs text-gray-600">{followupAction}</span>
      )}
      {bullets.length > 1 ? (
        <ul className="text-xs text-gray-700 list-disc list-inside space-y-1 mt-0.5">
          {bullets.map((bullet) => (
            <li key={bullet}>
              <InlineText text={bullet} />
            </li>
          ))}
        </ul>
      ) : notEmptyString(possibleResolutions) && (
        <span className="text-xs text-gray-700">
          <InlineText text={possibleResolutions!} />
        </span>
      )}
    </div>
  );
}

const sectionTitle = (
  <div className="flex items-center gap-1.5">
    <ErrorOutlineIcon className="text-red-500 !text-base" />
    <span>Errors</span>
  </div>
);

export default function Errors({ errors }: Props) {
  const visibleErrors = errors.filter(
    (error) => notEmptyString(error.code)
      || notEmptyString(error.description)
      || notEmptyString(error.followupAction)
      || notEmptyString(error.possibleResolutions),
  );

  return visibleErrors.length === 0 ? null : (
    <Section title={sectionTitle} className="!border-red-300">
      <div className="flex flex-col">
        {visibleErrors.map((error) => (
          <ErrorRow key={`${error.code ?? ''}-${error.description ?? ''}`} error={error} />
        ))}
      </div>
    </Section>
  );
}
